import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { getFactRefreshStatus } from '../../../utils/organizationApi';
import { PROGRESS_POLL_MS, shortFailure } from './refreshModel';

/**
 * Polls GET /factRefresh/status for orgs with a refresh in flight.
 *
 *   track(orgId)          start polling (call right after starting a run, or
 *                         to adopt a run the summary says is in progress)
 *   statuses[orgId]       latest running status (+ receivedAt for a smooth
 *                         elapsed clock) while it runs
 *   onFinish(orgId, data) called once when a tracked run stops; `data` is the
 *                         idle status response, which carries the REAL outcome
 *                         (lastResult / lastStatus / lastError) — never assume
 *                         "not running" means "succeeded".
 */
export default function useRefreshTracker(onFinish) {
  const [statuses, setStatuses] = useState({});
  const tracked = useRef(new Set());
  const finishRef = useRef(onFinish);
  const mounted = useRef(true);
  const [, tick] = useState(0);

  useEffect(() => { finishRef.current = onFinish; }, [onFinish]);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const poll = useCallback(async (orgId) => {
    let data;
    try {
      data = await getFactRefreshStatus(orgId);
    } catch (_) {
      return; // transient — keep tracking and retry next tick
    }
    if (!mounted.current) return;
    if (data?.running) {
      setStatuses((prev) => ({ ...prev, [orgId]: { ...data, receivedAt: Date.now() } }));
      return;
    }
    const wasTracked = tracked.current.delete(orgId);
    setStatuses((prev) => {
      if (!prev[orgId]) return prev;
      const next = { ...prev };
      delete next[orgId];
      return next;
    });
    if (wasTracked && finishRef.current) finishRef.current(orgId, data || { running: false });
  }, []);

  const track = useCallback((orgId, initial) => {
    if (!orgId) return;
    tracked.current.add(orgId);
    if (initial?.running) {
      setStatuses((prev) => ({ ...prev, [orgId]: { ...initial, receivedAt: Date.now() } }));
    }
    poll(orgId);
  }, [poll]);

  useEffect(() => {
    const id = setInterval(() => {
      tracked.current.forEach((orgId) => poll(orgId));
    }, PROGRESS_POLL_MS);
    return () => clearInterval(id);
  }, [poll]);

  const anyRunning = Object.keys(statuses).length > 0;
  useEffect(() => {
    if (!anyRunning) return undefined;
    const id = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [anyRunning]);

  const isTracked = useCallback((orgId) => tracked.current.has(orgId), []);

  return { statuses, track, isTracked };
}

/** Server elapsed + wall-clock delta since it was received (skew-safe). */
export const liveElapsed = (status) => (Number(status?.elapsedMs) || 0) + (Date.now() - (status?.receivedAt || Date.now()));

/** Outcome of a finished run from an idle status response. */
export const outcomeOf = (data) => {
  const result = data?.lastResult;
  const status = String(result?.status || data?.lastStatus || '').toLowerCase();
  return {
    status: status || 'unknown',
    error: result?.error || data?.lastError || null,
    failedFacts: result?.failedFacts || [],
  };
};

/** Toast the REAL outcome of a finished refresh (bug D). */
export const announceOutcome = (name, data) => {
  const { status, error, failedFacts } = outcomeOf(data);
  const who = name ? ` — ${name}` : '';
  if (status === 'succeeded') toast.success(`Refresh complete${who}`);
  else if (status === 'partial') toast.error(`Refresh partly failed${who}: ${shortFailure(failedFacts, error)}`, { duration: 8000 });
  else if (status === 'failed') toast.error(`Refresh failed${who}${error ? `: ${String(error).slice(0, 160)}` : ''}`, { duration: 8000 });
  else toast(`Refresh finished${who}. Check Data refresh for the result.`);
};
